// New Project (NewProjectView on the Mac): a title and what it starts from — Novel, Novella, Short-story collection,
// Memoir, Nonfiction, Journal, Screenplay or Blank (the template carries the project's type) — then the project is
// made in this browser and put on the web (store/planNewProject.ts), and opens at its first scene. Or a Scrivener
// project, zipped or as its folder (store/importScrivener.ts; File ▸ Import ▸ Scrivener Project… on the Mac), or a Word
// document (store/importWord.ts; File ▸ Import ▸ Word Document…), imported in this browser: progress while it runs, then
// the report, then Open. A file the writer opened with Five Rooms for Windows (store/openedFiles.ts) arrives already
// chosen.
import { errorText } from '@writing-suite/client/browser/usage.ts';
import type { ScrivenerPackage } from '@writing-suite/core/import';
import { useRef, useState } from 'react';
import { announce, useApp } from '../../shell/context.ts';
import { formatRoute, navigate } from '../../shell/route.ts';
import { importPackage, uploadAttachments, type ImportedProject } from '../../store/importScrivener.ts';
import { importWordFile, wordFileFrom, type ImportedWordProject, type WordFile } from '../../store/importWord.ts';
import { isStageFormat, stageFormatForRegion, type ScriptFormat } from '@writing-suite/core';
import { buildProject, createProject, deviceLocale, projectTemplates } from '../../store/planNewProject.ts';
import { DialogShell } from './BeatBoard.tsx';
import { ImportReportView, ScrivenerPickers } from './ScrivenerReimport.tsx';

/** What New Project imports instead of starting from a template. */
export type ImportSource = { kind: 'scrivener'; pkg: ScrivenerPackage } | { kind: 'word'; file: WordFile };

export function NewProjectDialog({ author, opened, onClose }: { author: string | null; opened?: ImportSource; onClose: () => void }) {
  const app = useApp();
  const templates = projectTemplates();
  const [title, setTitle] = useState(opened?.kind === 'scrivener' ? opened.pkg.title : opened?.kind === 'word' ? opened.file.title : '');
  const [templateId, setTemplateId] = useState('novel');
  // A play's format: UK in Britain and Ireland, US everywhere else, as the Mac chooses it.
  const [stageFormat, setStageFormat] = useState<ScriptFormat>(() => stageFormatForRegion(deviceLocale().split('-')[1]));
  const [source, setSource] = useState<ImportSource | null>(opened ?? null);
  const pkg = source?.kind === 'scrivener' ? source.pkg : null;
  const word = source?.kind === 'word' ? source.file : null;
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [imported, setImported] = useState<{ result: ImportedProject | ImportedWordProject; from: 'Scrivener' | 'Word' } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const account = () => ({ accountId: app.account.accountId, avk: app.account.avk });

  const create = async () => {
    const template = templates.find((t) => t.id === templateId);
    const name = title.trim();
    if (!template || !name) return;
    setBusy(true);
    setError(null);
    try {
      const built = buildProject({ title: name, template, locale: deviceLocale(), author: author ?? 'Me',
        ...(isStageFormat(template.scriptFormat ?? 'screen') ? { scriptFormat: stageFormat } : {}) });
      await createProject(app.account.session.api, account(), built, name);
      await app.refreshProjects();
      announce(`Made “${name}”`);
      onClose();
      // Nothing written yet: the boards first, with the template's first scene chosen (as the Mac does).
      navigate(formatRoute({ name: 'project', projectId: built.projectId, room: 'plan', page: 'corkboard', nodeId: built.opening, arrangementId: null, params: {} }));
    } catch (e) {
      setError(errorText(e, 'projects'));
      setBusy(false);
    }
  };

  const runImport = async () => {
    if (!source) return;
    const name = title.trim() || (source.kind === 'scrivener' ? source.pkg.title : source.file.title);
    setBusy(true);
    setError(null);
    try {
      let result: ImportedProject | ImportedWordProject;
      if (source.kind === 'scrivener') {
        result = await importPackage(source.pkg, {
          author: author ?? 'Me',
          locale: deviceLocale(),
          title: name,
          progress: (done, total, item) => setProgress(total ? `Importing ${Math.min(done + 1, total)} of ${total}${item ? ` · ${item}` : ''}` : null),
        });
      } else {
        setProgress('Reading the document…');
        // Let the line show before the import holds the page.
        await new Promise((resolve) => setTimeout(resolve, 0));
        result = importWordFile(source.file, { author: author ?? 'Me', locale: deviceLocale(), title: name });
      }
      setProgress('Putting the project on the web…');
      const key = await createProject(app.account.session.api, account(), result.built, name);
      if (result.attachments.length) {
        setProgress(`Uploading ${result.attachments.length === 1 ? 'one file' : `${result.attachments.length} files`}…`);
        try {
          await uploadAttachments(app.account.session.api, result.built.projectId, key, result.attachments);
        } catch (e) {
          const what = source.kind === 'scrivener' ? 'Scrivener’s files' : 'the pictures';
          result.report.issues.push({ item: 'Attachments', message: `Couldn’t upload ${what}: ${errorText(e, 'projects')}` });
        }
      }
      await app.refreshProjects();
      announce(`Imported “${name}”`);
      setImported({ result, from: source.kind === 'scrivener' ? 'Scrivener' : 'Word' });
    } catch (e) {
      setError(e instanceof Error && e.name !== 'Error' ? e.message : errorText(e, 'projects'));
    } finally {
      setProgress(null);
      setBusy(false);
    }
  };

  const open = () => {
    if (!imported) return;
    onClose();
    const { built } = imported.result;
    navigate(formatRoute({ name: 'project', projectId: built.projectId, room: 'write', page: 'write', nodeId: built.opening, arrangementId: null, params: {} }));
  };

  if (imported) {
    return (
      <DialogShell title={`Imported from ${imported.from}`} onCancel={onClose} className="new-project">
        <ImportReportView report={imported.result.report} />
        <div className="actions">
          <button type="button" className="button secondary" onClick={onClose}>
            Done
          </button>
          <button type="button" className="button primary" onClick={open} data-open-imported>
            Open the Project
          </button>
        </div>
      </DialogShell>
    );
  }

  return (
    <DialogShell title="New Project" onCancel={onClose} className="new-project">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void (source ? runImport() : create());
        }}
      >
        <label className="field-label stacked-label" htmlFor="new-project-title">
          Title
        </label>
        <input id="new-project-title" type="text" value={title} autoFocus onChange={(e) => setTitle(e.target.value)} />
        <fieldset className="template-choices" disabled={!!source}>
          <legend className="field-label">Start from</legend>
          {templates.map((t) => (
            <label key={t.id} className={`template-choice${templateId === t.id && !source ? ' chosen' : ''}`} data-template={t.id}>
              <input type="radio" name="template" value={t.id} checked={templateId === t.id && !source} onChange={() => setTemplateId(t.id)} />
              <span className="template-name">{t.name}</span>
              <span className="template-summary">{t.summary}</span>
            </label>
          ))}
        </fieldset>
        {isStageFormat(templates.find((t) => t.id === templateId)?.scriptFormat ?? 'screen') && !source ? (
          <fieldset className="stage-format" data-stage-format>
            <legend className="field-label">Format</legend>
            <label>
              <input type="radio" name="stage-format" value="stage-us" checked={stageFormat === 'stage-us'} onChange={() => setStageFormat('stage-us')} />
              US manuscript format <span className="template-summary">Names above their speeches, directions indented in brackets, on US Letter.</span>
            </label>
            <label>
              <input type="radio" name="stage-format" value="stage-uk" checked={stageFormat === 'stage-uk'} onChange={() => setStageFormat('stage-uk')} />
              UK manuscript format <span className="template-summary">Names beside their speeches, directions in italics, on A4.</span>
            </label>
          </fieldset>
        ) : null}
        {word ? null : (
          <div className="scrivener-import" data-scrivener-import>
            <span className="field-label">Or import a Scrivener project</span>
            {pkg ? (
              <p className="scrivener-chosen">
                <strong>{pkg.title}</strong> · {pkg.allItems().length} items
                <button type="button" className="link-button" onClick={() => setSource(null)} disabled={busy}>
                  Choose another
                </button>
              </p>
            ) : (
              <ScrivenerPickers busy={busy} onPackage={(p) => { setSource({ kind: 'scrivener', pkg: p }); setError(null); if (!title.trim()) setTitle(p.title); }} onError={(m) => { setSource(null); setError(m); }} />
            )}
            <p className="scrivener-note">The project is read in this browser; its documents, snapshots, media and comments go to your library on the web.</p>
          </div>
        )}
        {pkg ? null : (
          <div className="scrivener-import" data-word-import>
            <span className="field-label">Or import a Word document</span>
            {word ? (
              <p className="scrivener-chosen">
                <strong>{word.filename}.docx</strong> · {word.summary}, {word.words.toLocaleString()} words
                <button type="button" className="link-button" onClick={() => setSource(null)} disabled={busy}>
                  Choose another
                </button>
              </p>
            ) : (
              <WordPicker busy={busy} onFile={(f) => { setSource({ kind: 'word', file: f }); setError(null); if (!title.trim()) setTitle(f.title); }} onError={(m) => { setSource(null); setError(m); }} />
            )}
            <p className="scrivener-note">Headings become chapters and scenes; comments, links, footnotes and pictures come too. The file is read in this browser.</p>
          </div>
        )}
        {progress ? (
          <p className="import-progress" role="status" data-import-progress>
            {progress}
          </p>
        ) : null}
        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button primary" disabled={busy || (!source && !title.trim())} data-create-project>
            {busy ? (source ? 'Importing…' : 'Making…') : source ? 'Import' : 'Create'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

/** A Word document from the computer: read far enough to name it, or the Mac's message for a .doc or another kind of file. */
function WordPicker({ busy, onFile, onError }: { busy: boolean; onFile: (file: WordFile) => void; onError: (message: string) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <p className="scrivener-pickers">
        <button type="button" className="button secondary" onClick={() => input.current?.click()} disabled={busy}>
          Choose a Word Document…
        </button>
      </p>
      <input
        ref={input}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        hidden
        data-word-file
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          void file.arrayBuffer().then(
            (buffer) => onFile(wordFileFrom(file.name, new Uint8Array(buffer))),
          ).catch((err: unknown) => onError(err instanceof Error ? err.message : String(err)));
        }}
      />
    </>
  );
}
